'use client';

import {
  Popover,
  PopoverAnchor,
  PopoverArrow,
  PopoverContent,
  PopoverTrigger,
} from '@ria-client/tw-shadcn/components/popover';
import { useState, type ReactElement, type ReactNode } from 'react';

export interface DisclosureTooltipProps {
  children: ReactNode;
  trigger: ReactElement | ((toggle: () => void) => ReactElement);
  anchorEl?: HTMLElement | null;
  onOpen?: (open: boolean) => void;
  keepMounted?: boolean;
  allowHoverInteraction?: boolean;
  placement?: 'top' | 'right' | 'bottom' | 'left';
  zIndex?: number;
  popperMarginTop?: string | number;
  className?: string;
}

const isTouchDevice = () => typeof window !== 'undefined' && 'ontouchstart' in window;

export function DisclosureTooltip({
  children,
  trigger,
  anchorEl,
  onOpen,
  keepMounted = false,
  allowHoverInteraction = false,
  placement = 'bottom',
  zIndex,
  popperMarginTop,
  className,
}: DisclosureTooltipProps): React.JSX.Element {
  const [open, setOpen] = useState(false);
  const [keepOpen, setKeepOpen] = useState(false);
  const hoverEnabled = allowHoverInteraction && !isTouchDevice();
  const parsedPopperMarginTop =
    typeof popperMarginTop === 'string' ? Number.parseFloat(popperMarginTop) : -5;
  const sideOffset =
    typeof popperMarginTop === 'number'
      ? popperMarginTop
      : Number.isNaN(parsedPopperMarginTop)
        ? -5
        : parsedPopperMarginTop;

  const setOpenState = (next: boolean) => {
    setOpen(next);
    onOpen?.(next);
    if (!next) setKeepOpen(false);
  };
  const toggle = () => {
    setKeepOpen(true);
    setOpenState(!open);
  };
  const hoverHandlers = hoverEnabled
    ? {
        onMouseEnter: () => setOpenState(true),
        onMouseLeave: () => {
          if (!keepOpen) setOpenState(false);
        },
      }
    : {};

  const triggerNode = typeof trigger === 'function' ? trigger(toggle) : trigger;

  return (
    <Popover open={open} onOpenChange={setOpenState}>
      {anchorEl ? <PopoverAnchor virtualRef={{ current: anchorEl }} /> : null}
      <PopoverTrigger asChild onClick={() => setKeepOpen(true)} {...hoverHandlers}>
        {triggerNode}
      </PopoverTrigger>
      <PopoverContent
        side={placement}
        align="center"
        sideOffset={sideOffset}
        forceMount={keepMounted || undefined}
        collisionPadding={{ left: 20 }}
        style={zIndex === undefined ? undefined : { zIndex }}
        className={[
          'w-auto max-w-[340px] rounded-none border-[var(--content-neutral-light)] bg-white',
          'px-4 py-3 text-left leading-[1.8] text-foreground shadow-tooltip',
          className ?? '',
        ].join(' ')}
      >
        {children}
        <PopoverArrow
          className="fill-white stroke-[var(--content-neutral-light)] stroke-1"
          width={22}
          height={11}
        />
      </PopoverContent>
    </Popover>
  );
}
